// src/pages/AvailableVolunteerEvents.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config.js';
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query, 
  where,
  orderBy,
  arrayUnion,
  arrayRemove,
  getDoc
} from 'firebase/firestore';
import { format, isToday, isTomorrow, startOfDay, isBefore, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, getDay, parse } from 'date-fns';
// Import calendar services (adjust path based on your project structure)
// import { calendarService as googleCalendarService } from '../firebase/config.js';
// import { outlookCalendarService } from '../firebase/config.js';

// Helper function to format time based on tenant preference
const formatTimeDisplay = (timeString, use12Hour = true) => {
  if (!timeString) return '';
  
  // Parse time string (assumes HH:mm format like "15:30" or "09:00")
  const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
  
  if (isNaN(hours) || isNaN(minutes)) return timeString;
  
  if (!use12Hour) {
    // Return in 24-hour format
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
  
  // Convert to 12-hour format
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Helper function to format phone number for display
const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');
  // Format as (XXX) XXX-XXXX for US numbers
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

// Calendar Component for Volunteer Events
const Calendar = ({ availableEvents, onDateClick, selectedDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get events for a specific date
  const getEventsForDate = (date) => {
    return availableEvents.filter(event => 
      event.date && isSameDay(event.date, date)
    );
  };

  // Generate calendar grid with proper week alignment
  const generateCalendarGrid = () => {
    const firstDayOfWeek = getDay(monthStart);
    const grid = [];
    
    // Add empty cells for days before the month starts
    for (let i = 0; i < firstDayOfWeek; i++) {
      grid.push(null);
    }
    
    // Add all days of the month
    days.forEach(day => {
      grid.push(day);
    });
    
    return grid;
  };

  const calendarGrid = generateCalendarGrid();

  const goToPreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  return (
    <div style={{ 
      border: '1px solid #ddd', 
      borderRadius: '8px', 
      padding: '20px',
      backgroundColor: 'white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      {/* Calendar Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px' 
      }}>
        <button 
          onClick={goToPreviousMonth}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            padding: '8px 12px',
            cursor: 'pointer'
          }}
        >
          ← Previous
        </button>
        <h3 style={{ margin: 0 }}>
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <button 
          onClick={goToNextMonth}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            padding: '8px 12px',
            cursor: 'pointer'
          }}
        >
          Next →
        </button>
      </div>

      {/* Day Labels */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)', 
        gap: '1px',
        marginBottom: '5px'
      }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} style={{ 
            padding: '10px', 
            textAlign: 'center', 
            fontWeight: 'bold',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6'
          }}>
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)', 
        gap: '1px'
      }}>
        {calendarGrid.map((day, index) => {
          if (!day) {
            return (
              <div key={`empty-${index}`} style={{ 
                minHeight: '60px',
                border: '1px solid #dee2e6',
                backgroundColor: '#f8f9fa'
              }} />
            );
          }

          const eventsForDay = getEventsForDate(day);
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isCurrentDay = isToday(day);
          const isPastDay = isBefore(day, startOfDay(new Date()));

          return (
            <div
              key={day.toISOString()}
              onClick={() => !isPastDay && onDateClick(day)}
              style={{
                minHeight: '60px',
                border: '1px solid #dee2e6',
                padding: '5px',
                cursor: isPastDay ? 'default' : 'pointer',
                backgroundColor: isPastDay 
                  ? '#f8f9fa' 
                  : isSelected 
                    ? '#e3f2fd' 
                    : eventsForDay.length > 0 
                      ? '#fff3cd' 
                      : 'white',
                opacity: isPastDay ? 0.5 : 1,
                position: 'relative'
              }}
            >
              <div style={{ 
                fontWeight: isCurrentDay ? 'bold' : 'normal',
                color: isCurrentDay ? '#007bff' : isPastDay ? '#6c757d' : 'black'
              }}>
                {format(day, 'd')}
              </div>
              {eventsForDay.length > 0 && (
                <div style={{
                  fontSize: '10px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  borderRadius: '10px',
                  padding: '2px 6px',
                  position: 'absolute',
                  top: '2px',
                  right: '2px',
                  minWidth: '16px',
                  textAlign: 'center'
                }}>
                  {eventsForDay.length}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ marginTop: '15px', fontSize: '12px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #dee2e6' 
            }} />
            <span>Available Events</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#e3f2fd', 
              border: '1px solid #dee2e6' 
            }} />
            <span>Selected Date</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #dee2e6' 
            }} />
            <span>Past/Unavailable</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Event List Component
const EventList = ({ 
  events, 
  onSignUpClick, 
  showFormId, 
  selectedEvent, 
  volunteerInfo, 
  handleVolunteerInfoChange, 
  handleSubmit, 
  setShowFormId, 
  confirmationFormRef,
  currentUser,
  canAssignVolunteers,
  allVolunteers,
  selectedVolunteerForAssignment,
  setSelectedVolunteerForAssignment,
  tenantSettings,
  // New props for calendar integration
  addToCalendar,
  setAddToCalendar,
  userCalendarProvider,
  isSubmitting
}) => {
  if (!events || events.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
        No volunteer events available for this date.
      </div>
    );
  }

  // Determine time format preference
  const use12Hour = tenantSettings?.timeFormat === '12-hour' || tenantSettings?.timeFormat === undefined;

  return (
    <div style={{ marginTop: '20px' }}>
      {events.map(event => {
        const isEventDateInvalid = !event.date || isNaN(event.date.getTime());
        const spotsRemaining = event.volunteersNeeded - (event.volunteersAssigned?.length || 0);
        const isAlreadySignedUp = event.volunteersAssigned?.some(v => v.email === currentUser?.email);
        const isFull = spotsRemaining <= 0;
        
        return (
          <div key={event.id} style={{ marginBottom: '20px' }}>
            {/* Event Header */}
            <div style={{
              backgroundColor: '#e9ecef',
              padding: '15px 20px',
              borderRadius: '8px 8px 0 0',
              borderBottom: '2px solid #dee2e6'
            }}>
              <h4 style={{ margin: '0 0 5px 0', color: '#495057', fontSize: '1.2em' }}>
                {event.title}
              </h4>
              <p style={{ margin: '0 0 5px 0', color: '#6c757d', fontSize: '0.9em' }}>
                {event.date && !isNaN(event.date.getTime()) 
                  ? format(event.date, 'EEEE, MMMM d, yyyy')
                  : 'Invalid Date'
                }
                {event.date && !isNaN(event.date.getTime()) && (
                  <span> at {use12Hour ? format(event.date, 'h:mm a') : format(event.date, 'HH:mm')}</span>
                )}
                {event.endTime && <span> - {formatTimeDisplay(event.endTime, use12Hour)}</span>}
              </p>
              <p style={{ margin: '0 0 5px 0', color: '#6c757d', fontSize: '0.9em' }}>
                📍 {event.location}
              </p>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginTop: '10px' 
              }}>
                <span style={{ 
                  fontSize: '0.9em', 
                  color: spotsRemaining > 0 ? '#28a745' : '#dc3545',
                  fontWeight: 'bold'
                }}>
                  {spotsRemaining > 0 
                    ? `${spotsRemaining} spot${spotsRemaining > 1 ? 's' : ''} remaining` 
                    : 'Event is full'
                  }
                  <span style={{ color: '#6c757d', fontWeight: 'normal' }}>
                    {' '}({event.volunteersAssigned?.length || 0}/{event.volunteersNeeded} volunteers)
                  </span>
                </span>
                
                {isAlreadySignedUp && (
                  <span style={{ 
                    backgroundColor: '#d4edda', 
                    color: '#155724', 
                    padding: '4px 8px', 
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold'
                  }}>
                    ✓ You're signed up!
                  </span>
                )}
              </div>
            </div>

            {/* Event Details */}
            <div style={{
              border: '1px solid #ddd',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              backgroundColor: 'white',
              padding: '20px'
            }}>
              {event.description && (
                <div style={{ marginBottom: '15px', lineHeight: '1.5' }}>
                  <strong>Description:</strong> {event.description}
                </div>
              )}

              {event.skills?.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <strong>Skills needed:</strong> {event.skills.join(', ')}
                </div>
              )}

              {event.instructions && (
                <div style={{ 
                  marginBottom: '15px', 
                  padding: '12px', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '4px' 
                }}>
                  <strong>Special Instructions:</strong> {event.instructions}
                </div>
              )}

              {(event.contactPerson || event.contactEmail || event.contactPhone) && (
                <div style={{ marginBottom: '15px' }}>
                  <strong>Contact:</strong>{' '}
                  {event.contactPerson && <span>{event.contactPerson}</span>}
                  {event.contactEmail && <span> ({event.contactEmail})</span>}
                  {event.contactPhone && <span> - {event.contactPhone}</span>}
                </div>
              )}

              <div style={{ textAlign: 'right', display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Admin Assignment Section */}
                {canAssignVolunteers && !isFull && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <select
                      value={selectedVolunteerForAssignment}
                      onChange={(e) => setSelectedVolunteerForAssignment(e.target.value)}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '0.9em'
                      }}
                    >
                      <option value="">Select volunteer to assign...</option>
                      {allVolunteers.map(volunteer => (
                        <option key={volunteer.id} value={volunteer.email}>
                          {volunteer.name} ({volunteer.email})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => onSignUpClick(event, 'admin-assign')}
                      disabled={!selectedVolunteerForAssignment}
                      style={{
                        backgroundColor: selectedVolunteerForAssignment ? '#17a2b8' : '#6c757d',
                        color: 'white',
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: selectedVolunteerForAssignment ? 'pointer' : 'not-allowed',
                        fontSize: '0.9em'
                      }}
                    >
                      Assign Volunteer
                    </button>
                  </div>
                )}

                {/* Regular User Actions */}
                {isAlreadySignedUp ? (
                  <button
                    onClick={() => onSignUpClick(event, 'withdraw')}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.9em'
                    }}
                  >
                    Withdraw from Event
                  </button>
                ) : isFull ? (
                  <button
                    disabled
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'not-allowed',
                      fontSize: '0.9em'
                    }}
                  >
                    Event Full
                  </button>
                ) : (
                  <button
                    onClick={() => onSignUpClick(event, 'signup')}
                    style={{
                      backgroundColor: '#28a745',
                      color: 'white',
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.9em'
                    }}
                  >
                    Sign Up to Volunteer
                  </button>
                )}
              </div>

              {showFormId === event.id && (
                <div 
                  ref={confirmationFormRef} 
                  style={{ 
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1000,
                    backgroundColor: 'white',
                    padding: '30px',
                    border: '2px solid #ccc',
                    borderRadius: '12px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    maxWidth: '500px',
                    width: '90%',
                    maxHeight: '90vh',
                    overflowY: 'auto'
                  }}
                >
                  <h3 style={{ marginTop: '0', marginBottom: '20px' }}>
                    {volunteerInfo.action === 'signup' ? 'Confirm Volunteer Signup' : 
                     volunteerInfo.action === 'admin-assign' ? 'Confirm Volunteer Assignment' :
                     'Confirm Withdrawal'}
                  </h3>
                  
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Event:</strong> {selectedEvent?.title}
                  </div>
                  
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Date & Time:</strong> {selectedEvent?.date && format(selectedEvent.date, 'EEEE, MMMM d, yyyy')} at {selectedEvent?.date && (use12Hour ? format(selectedEvent.date, 'h:mm a') : format(selectedEvent.date, 'HH:mm'))}
                    {selectedEvent?.endTime && <span> - {formatTimeDisplay(selectedEvent.endTime, use12Hour)}</span>}
                  </div>

                  {volunteerInfo.action === 'signup' && (
                    <>
                      <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="volunteer-name" style={{ display: 'block', marginBottom: '5px' }}>
                          <strong>Your Name *</strong>
                        </label>
                        <input 
                          type="text"
                          id="volunteer-name"
                          name="name"
                          value={volunteerInfo.name}
                          onChange={handleVolunteerInfoChange}
                          required
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                        />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="volunteer-email" style={{ display: 'block', marginBottom: '5px' }}>
                          <strong>Your Email *</strong>
                        </label>
                        <input 
                          type="email"
                          id="volunteer-email"
                          name="email"
                          value={volunteerInfo.email}
                          onChange={handleVolunteerInfoChange}
                          required
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                        />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="volunteer-phone" style={{ display: 'block', marginBottom: '5px' }}>
                          <strong>Your Phone Number</strong>
                          <span style={{ fontSize: '0.8em', color: '#6c757d', marginLeft: '5px' }}>(for SMS reminders)</span>
                        </label>
                        <input 
                          type="tel"
                          id="volunteer-phone"
                          name="phone"
                          value={volunteerInfo.phone}
                          onChange={handleVolunteerInfoChange}
                          placeholder="+1 (555) 123-4567"
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                        />
                        <small style={{ color: '#6c757d', fontSize: '0.8em' }}>
                          You'll receive SMS reminders for this event. Reply STOP to opt out.
                        </small>
                      </div>

                      {/* Calendar Integration Option */}
                      {userCalendarProvider && userCalendarProvider !== 'none' && (
                        <div style={{ 
                          marginBottom: '20px', 
                          padding: '12px', 
                          backgroundColor: '#e8f4f8', 
                          borderRadius: '4px',
                          border: '1px solid #bee5eb'
                        }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={addToCalendar}
                              onChange={(e) => setAddToCalendar(e.target.checked)}
                              style={{ width: '18px', height: '18px' }}
                            />
                            <span>
                              <strong>Add to my {userCalendarProvider === 'google' ? 'Google' : 'Outlook'} Calendar</strong>
                              <br />
                              <small style={{ color: '#6c757d' }}>
                                A calendar event will be created automatically
                              </small>
                            </span>
                          </label>
                        </div>
                      )}

                      {/* SMS Notification Info */}
                      {currentUser?.smsOptIn && currentUser?.phoneNumber && (
                        <div style={{ 
                          marginBottom: '20px', 
                          padding: '12px', 
                          backgroundColor: '#d4edda', 
                          borderRadius: '4px',
                          border: '1px solid #c3e6cb',
                          fontSize: '0.9em'
                        }}>
                          📱 <strong>SMS notifications enabled</strong>
                          <br />
                          <small>You'll receive a text reminder at {formatPhoneNumber(currentUser.phoneNumber)}</small>
                        </div>
                      )}
                    </>
                  )}

                  {volunteerInfo.action === 'admin-assign' && (
                    <>
                      <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#e8f4f8', borderRadius: '4px' }}>
                        <strong>Assigning volunteer:</strong> {volunteerInfo.name} ({volunteerInfo.email})
                      </div>
                      <div style={{ marginBottom: '20px', color: '#17a2b8' }}>
                        You are assigning this volunteer to the event as an administrator.
                      </div>
                    </>
                  )}

                  {volunteerInfo.action === 'withdraw' && (
                    <>
                      <div style={{ marginBottom: '20px', color: '#dc3545' }}>
                        Are you sure you want to withdraw from this volunteer event?
                      </div>
                      {/* Show calendar removal notice if user has calendar connected */}
                      {userCalendarProvider && userCalendarProvider !== 'none' && (
                        <div style={{ 
                          marginBottom: '20px', 
                          padding: '12px', 
                          backgroundColor: '#fff3cd', 
                          borderRadius: '4px',
                          border: '1px solid #ffc107',
                          fontSize: '0.9em'
                        }}>
                          📅 The calendar event will be removed from your {userCalendarProvider === 'google' ? 'Google' : 'Outlook'} Calendar.
                        </div>
                      )}
                    </>
                  )}

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      style={{
                        backgroundColor: isSubmitting 
                          ? '#6c757d' 
                          : volunteerInfo.action === 'withdraw' 
                            ? '#dc3545' 
                            : volunteerInfo.action === 'admin-assign' 
                              ? '#17a2b8' 
                              : '#28a745',
                        color: 'white',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        fontSize: '1em',
                        flex: 1
                      }}
                    >
                      {isSubmitting 
                        ? 'Processing...' 
                        : volunteerInfo.action === 'signup' 
                          ? 'Confirm Signup' 
                          : volunteerInfo.action === 'admin-assign' 
                            ? 'Confirm Assignment' 
                            : 'Confirm Withdrawal'}
                    </button>
                    <button
                      type="button" 
                      onClick={() => setShowFormId(null)}
                      disabled={isSubmitting}
                      style={{
                        backgroundColor: '#6c757d', 
                        color: 'white',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        fontSize: '1em',
                        flex: 1
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Backdrop for modal */}
      {showFormId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999
          }}
          onClick={() => setShowFormId(null)}
        />
      )}
    </div>
  );
};

const AvailableVolunteerEvents = (props) => {
  const { currentUser } = props;
  const navigate = useNavigate();

  const [groupedEvents, setGroupedEvents] = useState({});
  const [allAvailableEvents, setAllAvailableEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [volunteerInfo, setVolunteerInfo] = useState({ 
    name: '', 
    email: '', 
    phone: '',
    action: 'signup' // 'signup', 'withdraw', or 'admin-assign'
  });
  const [showFormId, setShowFormId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('calendar');
  const [tenantSettings, setTenantSettings] = useState({});

  // Admin functionality state
  const [allVolunteers, setAllVolunteers] = useState([]);
  const [selectedVolunteerForAssignment, setSelectedVolunteerForAssignment] = useState('');

  // Calendar integration state
  const [addToCalendar, setAddToCalendar] = useState(true); // Default to true if user has calendar connected
  const [userCalendarProvider, setUserCalendarProvider] = useState(null); // 'google', 'outlook', or 'none'

  const confirmationFormRef = useRef(null);

  // Determine if current user can assign volunteers (admins/managers)
  const canAssignVolunteers = currentUser?.isSuperAdmin || 
                              currentUser?.role === 'system administrator' || 
                              currentUser?.role === 'educational administrator' ||
                              currentUser?.role === 'volunteer manager';

  // Fetch tenant settings
  const fetchTenantSettings = useCallback(async () => {
    if (!currentUser?.tenantId) return;

    try {
      const tenantDoc = await getDocs(query(
        collection(db, 'tenants'),
        where("__name__", "==", currentUser.tenantId)
      ));
      
      if (!tenantDoc.empty) {
        const settings = tenantDoc.docs[0].data();
        setTenantSettings(settings);
      }
    } catch (err) {
      console.error("Error fetching tenant settings:", err);
      // Default to 12-hour format if error
      setTenantSettings({ timeFormat: '12-hour' });
    }
  }, [currentUser?.tenantId]);

  // Fetch user's calendar preference
  const fetchUserCalendarPreference = useCallback(async () => {
    if (!currentUser?.tenantId || !currentUser?.uid) return;

    try {
      // First check tenant-level calendar setting
      const tenantCalendarSystem = tenantSettings?.calendarSystem || 'none';
      
      // Then check user-level override (if they have connected their own calendar)
      const userDocRef = doc(db, 'volunteers', currentUser.firestoreDocId || currentUser.docId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        // User can have their own calendar preference that overrides tenant default
        const userCalendar = userData.calendarProvider || tenantCalendarSystem;
        
        // Check if user has valid calendar tokens
        if (userCalendar === 'google' && userData.googleCalendarTokens) {
          setUserCalendarProvider('google');
        } else if (userCalendar === 'outlook' && userData.outlookCalendarTokens) {
          setUserCalendarProvider('outlook');
        } else {
          setUserCalendarProvider('none');
        }
      } else {
        // Check faculty_staff collection as fallback
        const facultyDocRef = doc(db, 'faculty_staff', currentUser.firestoreDocId || currentUser.docId);
        const facultyDoc = await getDoc(facultyDocRef);
        
        if (facultyDoc.exists()) {
          const userData = facultyDoc.data();
          const userCalendar = userData.calendarProvider || tenantCalendarSystem;
          
          if (userCalendar === 'google' && userData.googleCalendarTokens) {
            setUserCalendarProvider('google');
          } else if (userCalendar === 'outlook' && userData.outlookCalendarTokens) {
            setUserCalendarProvider('outlook');
          } else {
            setUserCalendarProvider('none');
          }
        }
      }
    } catch (err) {
      console.error("Error fetching user calendar preference:", err);
      setUserCalendarProvider('none');
    }
  }, [currentUser?.tenantId, currentUser?.uid, currentUser?.firestoreDocId, currentUser?.docId, tenantSettings?.calendarSystem]);

  // Fetch all volunteers for admin assignment
  const fetchAllVolunteers = useCallback(async () => {
    if (!currentUser?.tenantId || !canAssignVolunteers) return;

    try {
      const volunteersQuery = query(
        collection(db, 'volunteers'),
        where("tenantId", "==", currentUser.tenantId),
        where("status", "==", "approved")
      );
      
      const snapshot = await getDocs(volunteersQuery);
      const volunteersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setAllVolunteers(volunteersData);
    } catch (err) {
      console.error("Error fetching volunteers:", err);
    }
  }, [currentUser?.tenantId, canAssignVolunteers]);

  const fetchVolunteerEvents = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    if (!currentUser || !currentUser.tenantId) {
      console.warn("AvailableVolunteerEvents: Cannot fetch events, currentUser or tenantId is missing.");
      setGroupedEvents({});
      setAllAvailableEvents([]);
      setIsLoading(false);
      setError("Could not load volunteer events: user or organization information missing.");
      return;
    }

    try {
      const eventsQuery = query(
        collection(db, 'volunteerEvents'),
        where("tenantId", "==", currentUser.tenantId),
        where("status", "==", "active"),
        orderBy("date", "asc")
      );
      
      const snapshot = await getDocs(eventsQuery);
      const events = [];
      const todayForFilter = startOfDay(new Date());

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const eventId = docSnap.id;

        let eventDateObject;
        if (data.date) {
          // Handle Firestore Timestamp
          eventDateObject = data.date.toDate ? data.date.toDate() : new Date(data.date);
          if (isNaN(eventDateObject.getTime())) {
            eventDateObject = null;
          }
        } else {
          eventDateObject = null;
        }

        // Only show future events
        if (!eventDateObject || isBefore(eventDateObject, todayForFilter)) {
          continue;
        }

        events.push({
          id: eventId,
          ...data,
          date: eventDateObject
        });
      }
      
      setAllAvailableEvents(events);
      
      // Group events by date
      const grouped = {};
      const unknownDateLabel = "Events with Invalid/Missing Dates";
      
      for (const event of events) {
        let label;
        if (event.date && !isNaN(event.date.getTime())) {
          const eventDate = event.date;
          if (isToday(eventDate)) label = `Today, ${format(eventDate, 'MMMM d')}`;
          else if (isTomorrow(eventDate)) label = `Tomorrow, ${format(eventDate, 'MMMM d')}`;
          else label = `${format(eventDate, 'MMMM d, yyyy')}`;
        } else { 
          label = unknownDateLabel; 
        }
        
        if (!grouped[label]) grouped[label] = [];
        grouped[label].push(event);
      }
      
      setGroupedEvents(grouped);
    } catch (err) { 
      console.error("Error fetching volunteer events:", err);
      setError("Could not load available volunteer events.");
    } finally { 
      setIsLoading(false); 
    }
  }, [currentUser]); 

  useEffect(() => {
    if (currentUser && currentUser.tenantId) { 
      fetchTenantSettings();
      fetchVolunteerEvents();
      if (canAssignVolunteers) {
        fetchAllVolunteers();
      }
    } else {
      setGroupedEvents({});
      setAllAvailableEvents([]);
      setIsLoading(false);
    }
  }, [currentUser, fetchVolunteerEvents, fetchAllVolunteers, fetchTenantSettings, canAssignVolunteers, showFormId]); 

  // Fetch user calendar preference after tenant settings are loaded
  useEffect(() => {
    if (tenantSettings && currentUser) {
      fetchUserCalendarPreference();
    }
  }, [tenantSettings, currentUser, fetchUserCalendarPreference]);

  useEffect(() => {
    if (showFormId && confirmationFormRef.current) {
      confirmationFormRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center' 
      });
    }
  }, [showFormId]); 

  // Get events for selected date
  const getEventsForSelectedDate = () => {
    if (!selectedDate) return [];
    return allAvailableEvents.filter(event => 
      event.date && isSameDay(event.date, selectedDate)
    );
  };

  // Handle calendar date click
  const handleDateClick = (date) => {
    setSelectedDate(date);
    setViewMode('list');
  };

  const handleSignUpClick = (event, action) => {
    setSelectedEvent(event); 
    
    if (action === 'admin-assign') {
      // For admin assignment, get selected volunteer's info
      const selectedVolunteer = allVolunteers.find(v => v.email === selectedVolunteerForAssignment);
      if (!selectedVolunteer) {
        alert('Please select a volunteer to assign.');
        return;
      }
      
      setVolunteerInfo({
        name: selectedVolunteer.name,
        email: selectedVolunteer.email,
        phone: selectedVolunteer.phone || selectedVolunteer.phoneNumber || '',
        action: 'admin-assign'
      });
    } else {
      // For regular signup/withdraw, use current user's info
      // Check multiple phone field names for compatibility
      const userPhone = currentUser?.phoneNumber || currentUser?.mobileNumber || currentUser?.phone || '';
      
      setVolunteerInfo({
        name: currentUser?.displayName || currentUser?.name || '',
        email: currentUser?.email || '',
        phone: userPhone,
        action: action
      });
    }
    
    // Reset calendar checkbox to true when opening signup form
    if (action === 'signup') {
      setAddToCalendar(true);
    }
    
    setShowFormId(event.id);
  };
  
  const handleVolunteerInfoChange = (e) => {
    const { name, value } = e.target;
    setVolunteerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * Create calendar event for the volunteer signup
   * This integrates with either Google Calendar or Outlook Calendar
   */
  const createCalendarEvent = async (event, volunteerData) => {
    if (!addToCalendar || userCalendarProvider === 'none') {
      console.log("Calendar integration skipped - user preference or no provider");
      return null;
    }

    try {
      // Build event data for calendar
      const calendarEventData = {
        title: `Volunteer: ${event.title}`,
        description: `Volunteer Event: ${event.title}\n\nLocation: ${event.location || 'TBD'}\n\n${event.description || ''}\n\nContact: ${event.contactPerson || ''} ${event.contactEmail || ''} ${event.contactPhone || ''}`,
        location: event.location || '',
        startDateTime: event.date,
        endDateTime: event.endTime 
          ? new Date(event.date.getFullYear(), event.date.getMonth(), event.date.getDate(), 
              parseInt(event.endTime.split(':')[0]), parseInt(event.endTime.split(':')[1]))
          : new Date(event.date.getTime() + 2 * 60 * 60 * 1000), // Default 2 hours if no end time
        attendees: event.contactEmail ? [{ email: event.contactEmail }] : []
      };

      // Call the appropriate calendar service
      // NOTE: You'll need to import and initialize these services
      // import { calendarService as googleCalendarService, outlookCalendarService } from '../firebase/config.js';
      
      if (userCalendarProvider === 'google') {
        // const result = await googleCalendarService.createVolunteerEvent(calendarEventData);
        console.log("Would create Google Calendar event:", calendarEventData);
        // return result.eventId;
        return 'google-event-placeholder';
      } else if (userCalendarProvider === 'outlook') {
        // const result = await outlookCalendarService.createVolunteerEvent(calendarEventData);
        console.log("Would create Outlook Calendar event:", calendarEventData);
        // return result.id;
        return 'outlook-event-placeholder';
      }
    } catch (error) {
      console.error("Error creating calendar event:", error);
      // Don't fail the signup if calendar event creation fails
      // Just log the error and continue
      return null;
    }
  };

  /**
   * Delete calendar event when volunteer withdraws
   */
  const deleteCalendarEvent = async (event, volunteerData) => {
    if (userCalendarProvider === 'none') {
      return;
    }

    try {
      // Find the calendar event ID from the volunteer's signup record
      const calendarEventId = volunteerData.calendarEventId;
      
      if (!calendarEventId) {
        console.log("No calendar event ID found for this signup");
        return;
      }

      if (userCalendarProvider === 'google') {
        // await googleCalendarService.deleteEvent(calendarEventId);
        console.log("Would delete Google Calendar event:", calendarEventId);
      } else if (userCalendarProvider === 'outlook') {
        // await outlookCalendarService.deleteEvent(calendarEventId);
        console.log("Would delete Outlook Calendar event:", calendarEventId);
      }
    } catch (error) {
      console.error("Error deleting calendar event:", error);
      // Don't fail the withdrawal if calendar event deletion fails
    }
  };

  const handleSubmit = async () => {
    if (!selectedEvent || !currentUser) {
      alert("Missing required information.");
      return;
    }

    if (volunteerInfo.action === 'signup' || volunteerInfo.action === 'admin-assign') {
      if (!volunteerInfo.name || !volunteerInfo.email) {
        alert('Please provide volunteer name and email address.');
        return;
      }
    }
    
    setIsSubmitting(true);
    
    try {
      const eventDocRef = doc(db, 'volunteerEvents', selectedEvent.id);
      
      if (volunteerInfo.action === 'signup' || volunteerInfo.action === 'admin-assign') {
        // Create calendar event first (if applicable)
        let calendarEventId = null;
        if (volunteerInfo.action === 'signup' && addToCalendar && userCalendarProvider !== 'none') {
          calendarEventId = await createCalendarEvent(selectedEvent, volunteerInfo);
        }

        // Format phone number to E.164 format for SMS
        let formattedPhone = volunteerInfo.phone || '';
        if (formattedPhone) {
          // Remove all non-digits
          const digits = formattedPhone.replace(/\D/g, '');
          // Add +1 for US numbers if needed
          if (digits.length === 10) {
            formattedPhone = `+1${digits}`;
          } else if (digits.length === 11 && digits.startsWith('1')) {
            formattedPhone = `+${digits}`;
          }
        }

        // Add volunteer to the event
        const volunteerData = {
          name: volunteerInfo.name,
          email: volunteerInfo.email,
          phone: formattedPhone,
          signedUpAt: new Date().toISOString(),
          userId: volunteerInfo.action === 'admin-assign' ? 'admin-assigned' : currentUser.uid,
          assignedBy: volunteerInfo.action === 'admin-assign' ? currentUser.email : null,
          // SMS opt-in status (inherit from user profile or default to true)
          smsOptIn: currentUser?.smsOptIn !== false, // Default to true unless explicitly false
          // Calendar event reference (for later deletion if needed)
          calendarEventId: calendarEventId,
          calendarProvider: addToCalendar ? userCalendarProvider : null
        };
        
        await updateDoc(eventDocRef, {
          volunteersAssigned: arrayUnion(volunteerData)
        });
        
        const successMessage = volunteerInfo.action === 'admin-assign' 
          ? `Successfully assigned ${volunteerInfo.name} to "${selectedEvent.title}"!`
          : `Successfully signed up for "${selectedEvent.title}"!${calendarEventId ? ' Event added to your calendar.' : ''}`;
        alert(successMessage);

        // Note: SMS notification is triggered by a Cloud Function (sendVolunteerSignupNotification)
        // that listens to changes in the volunteerEvents collection
        
      } else {
        // Withdraw - first get the existing volunteer record
        const existingVolunteer = selectedEvent.volunteersAssigned?.find(v => v.email === currentUser.email);
        
        if (existingVolunteer) {
          // Delete calendar event if one was created
          await deleteCalendarEvent(selectedEvent, existingVolunteer);
          
          // Remove volunteer from the event
          await updateDoc(eventDocRef, {
            volunteersAssigned: arrayRemove(existingVolunteer)
          });
          
          alert(`Successfully withdrew from "${selectedEvent.title}".`);
        }
      }
      
      // Clear form state
      setShowFormId(null); 
      setVolunteerInfo({ name: '', email: '', phone: '', action: 'signup' });
      setSelectedEvent(null);
      setSelectedVolunteerForAssignment('');
      setAddToCalendar(true);
      
      // Refresh events
      await fetchVolunteerEvents();
      
    } catch (err) {
      console.error("Error updating volunteer signup:", err);
      alert('Failed to update signup. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && Object.keys(groupedEvents).length === 0) {
    return <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading available volunteer opportunities...</div>;
  }

  if (error) {
    return <div style={{ textAlign: 'center', marginTop: '50px', color: 'red' }}>Error: {error}</div>;
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '20px auto', padding: '0 15px' }}>
      <h1>Available Volunteer Opportunities for {currentUser?.displayName || currentUser?.name || 'User'}</h1>
      
      {/* User Notification Status Banner */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '15px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        alignItems: 'center'
      }}>
        {/* SMS Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ 
            width: '12px', 
            height: '12px', 
            borderRadius: '50%', 
            backgroundColor: currentUser?.smsOptIn && currentUser?.phoneNumber ? '#28a745' : '#6c757d' 
          }} />
          <span style={{ fontSize: '0.9em' }}>
            {currentUser?.smsOptIn && currentUser?.phoneNumber 
              ? `📱 SMS notifications: ${formatPhoneNumber(currentUser.phoneNumber)}` 
              : '📱 SMS notifications: Not configured'}
          </span>
        </div>
        
        {/* Calendar Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ 
            width: '12px', 
            height: '12px', 
            borderRadius: '50%', 
            backgroundColor: userCalendarProvider && userCalendarProvider !== 'none' ? '#28a745' : '#6c757d' 
          }} />
          <span style={{ fontSize: '0.9em' }}>
            {userCalendarProvider === 'google' 
              ? '📅 Google Calendar: Connected' 
              : userCalendarProvider === 'outlook' 
                ? '📅 Outlook Calendar: Connected' 
                : '📅 Calendar: Not connected'}
          </span>
        </div>
      </div>
      
      {/* View Toggle Buttons */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <button
          onClick={() => setViewMode('calendar')}
          style={{
            backgroundColor: viewMode === 'calendar' ? '#007bff' : '#6c757d',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          📅 Calendar View
        </button>
        <button
          onClick={() => setViewMode('list')}
          style={{
            backgroundColor: viewMode === 'list' ? '#007bff' : '#6c757d',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          📋 List View
        </button>
      </div>

      {Object.keys(groupedEvents).length === 0 && !isLoading ? (
        <p>No volunteer opportunities currently available.</p>
      ) : (
        <>
          {viewMode === 'calendar' ? (
            <div>
              <Calendar 
                availableEvents={allAvailableEvents}
                onDateClick={handleDateClick}
                selectedDate={selectedDate}
              />
              
              {selectedDate && (
                <div style={{ marginTop: '20px' }}>
                  <h2 style={{ 
                    borderBottom: '2px solid #007bff', 
                    paddingBottom: '10px', 
                    color: '#007bff' 
                  }}>
                    Events for {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                  </h2>
                  <EventList 
                    events={getEventsForSelectedDate()}
                    onSignUpClick={handleSignUpClick}
                    showFormId={showFormId}
                    selectedEvent={selectedEvent}
                    volunteerInfo={volunteerInfo}
                    handleVolunteerInfoChange={handleVolunteerInfoChange}
                    handleSubmit={handleSubmit}
                    setShowFormId={setShowFormId}
                    confirmationFormRef={confirmationFormRef}
                    currentUser={currentUser}
                    canAssignVolunteers={canAssignVolunteers}
                    allVolunteers={allVolunteers}
                    selectedVolunteerForAssignment={selectedVolunteerForAssignment}
                    setSelectedVolunteerForAssignment={setSelectedVolunteerForAssignment}
                    tenantSettings={tenantSettings}
                    addToCalendar={addToCalendar}
                    setAddToCalendar={setAddToCalendar}
                    userCalendarProvider={userCalendarProvider}
                    isSubmitting={isSubmitting}
                  />
                </div>
              )}
            </div>
          ) : (
            <div>
              {selectedDate && (
                <div style={{ 
                  marginBottom: '20px', 
                  padding: '15px', 
                  backgroundColor: '#e3f2fd', 
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>
                    Showing events for: <strong>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</strong>
                  </span>
                  <button
                    onClick={() => setSelectedDate(null)}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      padding: '5px 10px',
                      cursor: 'pointer'
                    }}
                  >
                    Clear Filter
                  </button>
                </div>
              )}
              
              {selectedDate ? (
                <div>
                  <EventList 
                    events={getEventsForSelectedDate()}
                    onSignUpClick={handleSignUpClick}
                    showFormId={showFormId}
                    selectedEvent={selectedEvent}
                    volunteerInfo={volunteerInfo}
                    handleVolunteerInfoChange={handleVolunteerInfoChange}
                    handleSubmit={handleSubmit}
                    setShowFormId={setShowFormId}
                    confirmationFormRef={confirmationFormRef}
                    currentUser={currentUser}
                    canAssignVolunteers={canAssignVolunteers}
                    allVolunteers={allVolunteers}
                    selectedVolunteerForAssignment={selectedVolunteerForAssignment}
                    setSelectedVolunteerForAssignment={setSelectedVolunteerForAssignment}
                    tenantSettings={tenantSettings}
                    addToCalendar={addToCalendar}
                    setAddToCalendar={setAddToCalendar}
                    userCalendarProvider={userCalendarProvider}
                    isSubmitting={isSubmitting}
                  />
                </div>
              ) : (
                Object.entries(groupedEvents).map(([label, eventsInGroup]) => (
                  <div key={label} style={{ marginBottom: '30px' }}>
                    <h2 style={{ borderBottom: '2px solid #eee', paddingBottom: '10px', marginBottom: '20px' }}>{label}</h2>
                    {eventsInGroup.length === 0 && label !== "Events with Invalid/Missing Dates" ? (
                      <p>No events for this date group.</p>
                    ) : (
                      <EventList 
                        events={eventsInGroup}
                        onSignUpClick={handleSignUpClick}
                        showFormId={showFormId}
                        selectedEvent={selectedEvent}
                        volunteerInfo={volunteerInfo}
                        handleVolunteerInfoChange={handleVolunteerInfoChange}
                        handleSubmit={handleSubmit}
                        setShowFormId={setShowFormId}
                        confirmationFormRef={confirmationFormRef}
                        currentUser={currentUser}
                        canAssignVolunteers={canAssignVolunteers}
                        allVolunteers={allVolunteers}
                        selectedVolunteerForAssignment={selectedVolunteerForAssignment}
                        setSelectedVolunteerForAssignment={setSelectedVolunteerForAssignment}
                        tenantSettings={tenantSettings}
                        addToCalendar={addToCalendar}
                        setAddToCalendar={setAddToCalendar}
                        userCalendarProvider={userCalendarProvider}
                        isSubmitting={isSubmitting}
                      />
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AvailableVolunteerEvents;